import { useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Field from './Field';
import { borderLabel, useLabelPlacement } from './labelPlacement';
import { describedBy, inputAriaLabel, inputAttrProps, isInvalid, useFieldIds, type BaseFieldProps } from './fieldUtils';
import './form.css';

/**
 * Number field. A text input underneath, so there is no browser spinner, no
 * "e" or "+", and the mouse wheel never changes the value. Only digits, one
 * decimal point (unless `decimals` is 0) and a leading minus (when `min`
 * allows negatives) can be typed. ArrowUp / ArrowDown step by `step`.
 *
 * Typing reports each complete in-range number as it is typed; a number
 * outside min/max is flagged and clamped when the field is left. With
 * `commitOnBlur`, nothing is reported until the field is left or Enter.
 *
 * onValueChanged fires only for user changes, never when `value` changes.
 */
export interface NumberFieldProps extends BaseFieldProps {
  /** The number, or null / '' for empty. A numeric string is read as a number. */
  value: number | string | null | undefined;
  /** The new number, or `emptyValue` when the box is emptied. User changes only. */
  onValueChanged: (value: number | null) => void;
  min?: number;
  max?: number;
  /** ArrowUp / ArrowDown and the step buttons. Default 1. */
  step?: number;
  /** Most digits after the point; 0 allows whole numbers only. Default: no limit. */
  decimals?: number;
  /** NumberBox `format`: "#0" is whole numbers; currency shows $ and fixed decimals;
   *  fixedPoint pads to `precision` decimals when the field is not focused
   *  ("#0.00" is { precision: 2 }, "#,##0.00##" is { precision: 2, maxPrecision: 4, grouping: true }). */
  format?: '#0' | { type: 'currency'; precision?: number } | { type: 'fixedPoint'; precision: number; maxPrecision?: number; grouping?: boolean };
  /** What an emptied box reports. Default null. AppTextBox reported 0. */
  emptyValue?: number | null;
  /** -/+ buttons inside the field. */
  showSpinButtons?: boolean;
  /** Clear button while there is a number; reports `emptyValue`. */
  showClearButton?: boolean;
  /** Report only when the field is left or Enter is pressed. */
  commitOnBlur?: boolean;
}

function toNumber(v: number | string | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

const keyOf = (n: number | null) => (n == null ? '' : String(n));

export default function NumberField(props: NumberFieldProps) {
  const {
    value, onValueChanged, min, max, step = 1, format, emptyValue = null, showSpinButtons = false, showClearButton = false, commitOnBlur = false,
    label, ariaLabel, placeholder, required, disabled, readOnly, helperText, errorText, width, height, className, inputAttr, labelPlacement,
  } = props;
  const ids = useFieldIds(inputAttr);
  const currency = typeof format === 'object' && format.type === 'currency';
  const fixed = typeof format === 'object' && format.type === 'fixedPoint' ? format : null;
  const decimals = props.decimals ?? (format === '#0' ? 0 : currency ? (format as { precision?: number }).precision ?? 2
    : fixed ? fixed.maxPrecision ?? fixed.precision : undefined);
  const num = toNumber(value);
  const key = keyOf(num);
  const allowNegative = min == null || min < 0;

  const [focused, setFocused] = useState(false);
  // Typed text, tied to the value it was typed over (see DateField).
  const [draft, setDraft] = useState<{ text: string; forKey: string } | null>(null);
  const live = draft && draft.forKey === key ? draft : null;

  const display = (n: number | null) => {
    if (n == null) return '';
    if (currency && !focused) {
      return new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n);
    }
    if (fixed && !focused) {
      const most = Math.max(fixed.precision, decimals ?? fixed.precision);
      return new Intl.NumberFormat('en-US', { minimumFractionDigits: fixed.precision, maximumFractionDigits: most, useGrouping: !!fixed.grouping }).format(n);
    }
    return decimals != null && !focused ? String(Number(n.toFixed(decimals))) : String(n);
  };
  const text = live ? live.text : display(num);

  const outOfRange = (n: number) => (min != null && n < min) || (max != null && n > max);
  const clamp = (n: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));
  const round = (n: number) => (decimals != null ? Number(n.toFixed(decimals)) : n);

  const report = (n: number | null) => {
    if (keyOf(n) !== key) onValueChanged(n);
  };

  const pattern = new RegExp(`^${allowNegative ? '-?' : ''}\\d*${decimals === 0 ? '' : `(\\.\\d${decimals != null ? `{0,${decimals}}` : '*'})?`}$`);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const t = e.target.value.replace(/,/g, '');
    if (!pattern.test(t)) return; // "e", "+", letters, a second point: ignored
    const n = t === '' ? null : /^-?\.?$/.test(t) ? undefined : Number(t);
    if (commitOnBlur || n === undefined || (n != null && outOfRange(n))) {
      setDraft({ text: t, forKey: key });
      return;
    }
    const next = n ?? emptyValue;
    setDraft({ text: t, forKey: keyOf(next) });
    report(next);
  };

  const commit = () => {
    const t = live?.text;
    setDraft(null);
    if (t == null) return;
    const n = t === '' ? null : /^-?\.?$/.test(t) ? null : Number(t);
    report(n == null ? emptyValue : round(clamp(n)));
  };

  const stepBy = (dir: 1 | -1) => {
    const base = live ? toNumber(live.text) ?? 0 : num ?? 0;
    setDraft(null);
    report(round(clamp(base + dir * step)));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (readOnly || disabled) return;
    if (e.key === 'ArrowUp') { e.preventDefault(); stepBy(1); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); stepBy(-1); }
    else if (e.key === 'Enter') commit();
  };

  const typedOut = live ? (() => { const n = toNumber(live.text); return n != null && outOfRange(n); })() : false;
  const invalid = isInvalid(props) || typedOut;
  const editable = !disabled && !readOnly;
  const name = label ?? ariaLabel ?? 'value';
  const placement = useLabelPlacement(labelPlacement);
  const outlineLabel = borderLabel(placement, label, required);

  return (
    <Field
      inputId={ids.id}
      label={label}
      required={required}
      helperText={helperText}
      helperId={ids.helperId}
      errorText={errorText}
      errorId={ids.errorId}
      error={invalid}
      disabled={disabled}
      width={width}
      height={height}
      labelPlacement={placement}
      className={`${readOnly ? 'app-field--readonly ' : ''}${className ?? ''}`.trim() || undefined}
    >
      <TextField
        id={ids.id}
        value={text}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); commit(); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        error={invalid}
        size="small"
        fullWidth
        label={outlineLabel}
        InputLabelProps={outlineLabel ? { shrink: true } : undefined}
        InputProps={{
          notched: !!outlineLabel,
          readOnly,
          startAdornment: currency ? <InputAdornment position="start">$</InputAdornment> : undefined,
          endAdornment: showSpinButtons || (showClearButton && editable && text !== '') ? (
            <InputAdornment position="end">
              {showClearButton && editable && text !== '' && (
                <IconButton size="small" edge={showSpinButtons ? undefined : 'end'} aria-label={`Clear ${name}`} onClick={() => { setDraft(null); report(emptyValue); }}>
                  <i className="bi bi-x-lg" aria-hidden="true" style={{ fontSize: 12 }} />
                </IconButton>
              )}
              {showSpinButtons && <span className="app-number-step">
                <IconButton size="small" aria-label={`Decrease ${name}`} disabled={!editable || (min != null && (num ?? 0) <= min)} onClick={() => stepBy(-1)}>
                  <i className="bi bi-dash-lg" aria-hidden="true" style={{ fontSize: 12 }} />
                </IconButton>
                <IconButton size="small" edge="end" aria-label={`Increase ${name}`} disabled={!editable || (max != null && (num ?? 0) >= max)} onClick={() => stepBy(1)}>
                  <i className="bi bi-plus-lg" aria-hidden="true" style={{ fontSize: 12 }} />
                </IconButton>
              </span>}
            </InputAdornment>
          ) : undefined,
        }}
        inputProps={{
          ...inputAttrProps(inputAttr),
          inputMode: decimals === 0 ? 'numeric' : 'decimal',
          autoComplete: 'off',
          'aria-label': inputAriaLabel(label, required, ariaLabel),
          'aria-required': required || undefined,
          'aria-invalid': invalid || undefined,
          'aria-describedby': describedBy(ids, { helperText, errorText }),
        }}
      />
    </Field>
  );
}
